import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  History, RefreshCw, Download, Camera, Filter, Calendar as CalendarIcon,
  Package, AlertTriangle, XCircle, Clock, Webhook, Zap, User as UserIcon,
  FileDown, GitCompareArrows, ArrowRight, Minus, Plus, Trash2, Sparkles,
  Briefcase, TrendingUp, TrendingDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BODEGAS = [
  { codigo: '200', nombre: 'Bodega 200 - H.San Juan' },
  { codigo: '201', nombre: 'Bodega 201 - H.Mexico' },
  { codigo: '202', nombre: 'Bodega 202 - H.Calderon' },
  { codigo: '203', nombre: 'Bodega 203 - HSRA Alajuela' },
  { codigo: '207', nombre: 'Bodega 207 - H.Puntarenas' },
  { codigo: '16', nombre: 'Bodega 16 - HSVP Count' },
  { codigo: 'HH', nombre: 'Bodega HH - Anaquel Heredia' },
];

const TRIGGER_LABELS = {
  webhook: { label: 'Webhook Bitua', icon: Webhook, className: 'bg-blue-100 text-blue-700 border-blue-300' },
  scheduler: { label: 'Programado 3PM', icon: Clock, className: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  manual: { label: 'Manual', icon: UserIcon, className: 'bg-purple-100 text-purple-700 border-purple-300' },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export const HistorialConsigna = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [total, setTotal] = useState(0);

  // Estado del comparador
  const [selectedIds, setSelectedIds] = useState([]); // máx 2
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null); // resultado backend
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareExporting, setCompareExporting] = useState(false);

  const [bodegaFilter, setBodegaFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [fechaInicio, setFechaInicio] = useState(daysAgoISO(30));
  const [fechaFin, setFechaFin] = useState(todayISO());

  const fetchSnapshots = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', offset: '0' });
      if (bodegaFilter) params.append('bodega_codigo', bodegaFilter);
      if (triggerFilter) params.append('triggered_by', triggerFilter);
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);

      const resp = await axios.get(`${API}/admin/historial-consigna?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnapshots(resp.data.items || []);
      setTotal(resp.data.total || 0);
    } catch (e) {
      console.error('Error fetching historial:', e);
      toast.error('Error al cargar historial de consigna');
    } finally {
      setLoading(false);
    }
  }, [token, bodegaFilter, triggerFilter, fechaInicio, fechaFin]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  const handleTakeSnapshot = async (bodegaCodigo = null) => {
    if (!token) return;
    setSnapping(true);
    try {
      const url = bodegaCodigo
        ? `${API}/admin/historial-consigna/snapshot-manual?bodega_codigo=${bodegaCodigo}`
        : `${API}/admin/historial-consigna/snapshot-manual`;
      const resp = await axios.post(url, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (bodegaCodigo) {
        toast.success(`📸 Snapshot guardado para Bodega ${bodegaCodigo}`);
      } else {
        const r = resp.data.result;
        toast.success(`📸 Snapshot guardado: ${r?.saved?.length || 0} bodegas, ${r?.failed?.length || 0} fallidas`);
      }
      await fetchSnapshots();
    } catch (e) {
      console.error('Error tomando snapshot:', e);
      toast.error(`Error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSnapping(false);
    }
  };

  const handleExportExcel = async () => {
    if (!token) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (bodegaFilter) params.append('bodega_codigo', bodegaFilter);
      if (triggerFilter) params.append('triggered_by', triggerFilter);
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);

      const resp = await axios.get(`${API}/admin/historial-consigna/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `historial_consigna_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('✅ Excel descargado');
    } catch (e) {
      console.error('Error exportando:', e);
      toast.error(`Error al exportar: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadSnapshotInventory = async (snapshot) => {
    if (!token) return;
    const id = snapshot._id;
    setDownloadingId(id);
    try {
      const resp = await axios.get(`${API}/admin/historial-consigna/${id}/inventario`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateSafe = (snapshot.fecha || 'snapshot').replace(/-/g, '');
      const hourSafe = (snapshot.hora || '').replace(/:/g, '');
      a.download = `inventario_bodega${snapshot.bodega_codigo}_${dateSafe}_${hourSafe}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`✅ Inventario de Bodega ${snapshot.bodega_codigo} descargado`);
    } catch (e) {
      console.error('Error descargando inventario:', e);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404 && detail) {
        // El backend manda blob con detail; intentar parsear
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed.detail || 'Snapshot sin detalle');
        } catch {
          toast.error('Este snapshot fue creado antes de la actualización y no contiene detalle. Genere uno nuevo.');
        }
      } else {
        toast.error(`Error: ${detail || e.message}`);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  // ============================================================
  // COMPARADOR
  // ============================================================
  const toggleSelect = (snapshot) => {
    const id = snapshot._id;
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) {
        toast.info('Solo se pueden comparar 2 snapshots. Deselecciona uno primero.');
        return prev;
      }
      // Validar misma bodega
      if (prev.length === 1) {
        const otherSnap = snapshots.find((s) => s._id === prev[0]);
        if (otherSnap && otherSnap.bodega_codigo !== snapshot.bodega_codigo) {
          toast.error(`No se puede comparar: el primer snapshot es de Bodega ${otherSnap.bodega_codigo} y este de Bodega ${snapshot.bodega_codigo}. Solo se comparan snapshots de la misma bodega.`);
          return prev;
        }
      }
      return [...prev, id];
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const handleCompare = async () => {
    if (selectedIds.length !== 2 || !token) return;
    setComparing(true);
    try {
      const [a, b] = selectedIds;
      const resp = await axios.get(
        `${API}/admin/historial-consigna/compare?snapshot_a=${a}&snapshot_b=${b}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComparison(resp.data);
      setShowCompareModal(true);
    } catch (e) {
      console.error('Error comparando:', e);
      toast.error(`Error al comparar: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setComparing(false);
    }
  };

  const handleCompareExport = async () => {
    if (selectedIds.length !== 2 || !token) return;
    setCompareExporting(true);
    try {
      const [a, b] = selectedIds;
      const resp = await axios.get(
        `${API}/admin/historial-consigna/compare/export?snapshot_a=${a}&snapshot_b=${b}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const bodega = comparison?.snapshot_a?.bodega_codigo || 'X';
      link.download = `comparativa_bodega${bodega}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('✅ Comparativa descargada');
    } catch (e) {
      console.error('Error exportando comparativa:', e);
      toast.error(`Error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCompareExporting(false);
    }
  };

  // KPIs agregados de los snapshots cargados
  const kpis = React.useMemo(() => {
    const acc = {
      registros: snapshots.length,
      bodegas: new Set(snapshots.map(s => s.bodega_codigo)).size,
      vencidos: snapshots.reduce((a, s) => a + (s.items_vencidos || 0), 0),
      por_vencer: snapshots.reduce((a, s) => a + (s.items_por_vencer_90d || 0), 0),
    };
    return acc;
  }, [snapshots]);

  return (
    <div className="space-y-6" data-testid="historial-consigna-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <History className="w-7 h-7 text-purple-600" />
            Historial de Consigna
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Registro histórico del inventario por bodega. Cada vez que Bitua actualiza
            (webhook), se guarda un snapshot con fecha y hora.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => handleTakeSnapshot(null)}
            disabled={snapping}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="snapshot-all-btn"
          >
            <Camera className={`w-4 h-4 mr-2 ${snapping ? 'animate-pulse' : ''}`} />
            {snapping ? 'Tomando…' : 'Tomar snapshot (todas)'}
          </Button>
          <Button
            onClick={handleExportExcel}
            disabled={exporting || snapshots.length === 0}
            variant="outline"
            data-testid="export-excel-btn"
          >
            <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exportando…' : 'Exportar Excel'}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-registros">
          <CardContent className="p-4 flex items-center gap-3">
            <Package className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-slate-800">{kpis.registros}</p>
              <p className="text-xs text-slate-600">Registros visibles</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-bodegas">
          <CardContent className="p-4 flex items-center gap-3">
            <Zap className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-slate-800">{kpis.bodegas}</p>
              <p className="text-xs text-slate-600">Bodegas con snapshot</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-vencidos">
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-red-700">{kpis.vencidos}</p>
              <p className="text-xs text-slate-600">SKUs vencidos (suma)</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-por-vencer">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-2xl font-bold text-amber-700">{kpis.por_vencer}</p>
              <p className="text-xs text-slate-600">SKUs por vencer (suma)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Bodega</label>
              <select
                value={bodegaFilter}
                onChange={(e) => setBodegaFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                data-testid="filter-bodega"
              >
                <option value="">Todas</option>
                {BODEGAS.map(b => (
                  <option key={b.codigo} value={b.codigo}>{b.codigo} — {b.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Origen</label>
              <select
                value={triggerFilter}
                onChange={(e) => setTriggerFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                data-testid="filter-trigger"
              >
                <option value="">Todos</option>
                <option value="webhook">Webhook Bitua</option>
                <option value="scheduler">Programado 3PM</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Desde</label>
              <Input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                data-testid="filter-fecha-inicio"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Hasta</label>
              <Input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                data-testid="filter-fecha-fin"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={fetchSnapshots}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="apply-filters-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Cargando…' : 'Aplicar'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Mostrando {snapshots.length} de {total} registros totales en el rango.
          </p>
        </CardContent>
      </Card>

      {/* Barra de comparador */}
      {selectedIds.length > 0 && (
        <Card className="bg-purple-50 border-purple-300" data-testid="compare-toolbar">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <GitCompareArrows className="w-5 h-5 text-purple-700" />
              <span className="font-semibold text-purple-800">
                {selectedIds.length === 1 ? 'Selecciona 1 snapshot más para comparar' : '2 snapshots seleccionados'}
              </span>
              <Badge className="bg-purple-200 text-purple-800 ml-2">{selectedIds.length}/2</Badge>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleCompare}
                disabled={selectedIds.length !== 2 || comparing}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="compare-btn"
              >
                <Sparkles className={`w-4 h-4 mr-2 ${comparing ? 'animate-pulse' : ''}`} />
                {comparing ? 'Comparando…' : 'Comparar'}
              </Button>
              <Button
                onClick={clearSelection}
                variant="outline"
                data-testid="clear-selection-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" /> Snapshots
          </CardTitle>
          <CardDescription>
            Ordenados del más reciente al más antiguo. Hora en zona Costa Rica.
            Selecciona 2 filas de la misma bodega para comparar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
              Cargando snapshots…
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="empty-state">
              <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay snapshots en este rango</p>
              <p className="text-sm mt-1">Ajusta los filtros o presiona &quot;Tomar snapshot&quot; para registrar el estado actual.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="historial-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold text-slate-700 text-center w-10">
                      <GitCompareArrows className="w-4 h-4 text-slate-500 mx-auto" />
                    </th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Fecha / Hora (CR)</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Bodega</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">Unidades</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">SKUs</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">En Cero</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">Bajo Mín.</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">Vencidos</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">Por Vencer</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Origen</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-center">Inventario</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {snapshots.map((s) => {
                    const trig = TRIGGER_LABELS[s.triggered_by] || TRIGGER_LABELS.manual;
                    const TrigIcon = trig.icon;
                    const isSelected = selectedIds.includes(s._id);
                    return (
                      <tr
                        key={s._id}
                        className={`hover:bg-slate-50 ${isSelected ? 'bg-purple-50' : ''}`}
                        data-testid={`row-${s._id}`}
                      >
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(s)}
                            className="w-4 h-4 accent-purple-600 cursor-pointer"
                            data-testid={`select-snapshot-${s._id}`}
                            title="Seleccionar para comparar"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">
                          {s.timestamp_cr}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-800">{s.bodega_codigo}</div>
                          <div className="text-xs text-slate-500">{s.bodega_nombre}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{(s.total_unidades || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{(s.total_skus || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-mono ${s.items_en_cero > 0 ? 'text-orange-700 font-bold' : 'text-slate-500'}`}>
                            {s.items_en_cero || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-mono ${s.items_bajo_minimo > 0 ? 'text-yellow-700 font-bold' : 'text-slate-500'}`}>
                            {s.items_bajo_minimo || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-mono ${s.items_vencidos > 0 ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                            {s.items_vencidos || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-mono ${s.items_por_vencer_90d > 0 ? 'text-amber-700 font-bold' : 'text-slate-500'}`}>
                            {s.items_por_vencer_90d || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={trig.className}>
                            <TrigIcon className="w-3 h-3 mr-1" />
                            {trig.label}
                          </Badge>
                          {s.event_type && (
                            <div className="text-xs text-slate-500 mt-1 font-mono">{s.event_type}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={downloadingId === s._id}
                            onClick={() => handleDownloadSnapshotInventory(s)}
                            className="hover:bg-purple-50 hover:border-purple-400"
                            data-testid={`download-inv-${s._id}`}
                            title="Descargar inventario de esta fecha"
                          >
                            <FileDown className={`w-4 h-4 ${downloadingId === s._id ? 'animate-pulse' : ''}`} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de comparación */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="compare-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-800">
              <GitCompareArrows className="w-6 h-6" />
              Comparativa de Inventario
            </DialogTitle>
            <DialogDescription>
              {comparison && (
                <span className="text-sm">
                  Bodega <strong>{comparison.snapshot_a.bodega_codigo}</strong> — {comparison.snapshot_a.bodega_nombre}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {comparison && (
            <div className="space-y-4">
              {/* Encabezado A vs B */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                <Card className="bg-slate-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Snapshot A (anterior)</p>
                    <p className="font-mono text-sm font-semibold">{comparison.snapshot_a.timestamp_cr}</p>
                    <p className="text-xs text-slate-600">{comparison.snapshot_a.total_unidades.toLocaleString()} unidades · {comparison.snapshot_a.total_skus} SKUs</p>
                  </CardContent>
                </Card>
                <ArrowRight className="w-8 h-8 text-purple-600 mx-auto" />
                <Card className="bg-purple-50 border-purple-300">
                  <CardContent className="p-3">
                    <p className="text-xs text-purple-700">Snapshot B (posterior)</p>
                    <p className="font-mono text-sm font-semibold">{comparison.snapshot_b.timestamp_cr}</p>
                    <p className="text-xs text-purple-700">{comparison.snapshot_b.total_unidades.toLocaleString()} unidades · {comparison.snapshot_b.total_skus} SKUs</p>
                  </CardContent>
                </Card>
              </div>

              {/* RESUMEN EJECUTIVO */}
              {comparison.executive && (
                <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200" data-testid="executive-summary">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-indigo-900">
                      <Briefcase className="w-4 h-4" />
                      Resumen Ejecutivo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-[10px] uppercase text-slate-500 tracking-wide">Periodo</p>
                        <p className="font-bold text-slate-800">{comparison.executive.dias_transcurridos > 1
                          ? `${comparison.executive.dias_transcurridos} días`
                          : `${comparison.executive.horas_transcurridas} hrs`}</p>
                      </div>
                      <div className="bg-emerald-100 rounded p-2">
                        <p className="text-[10px] uppercase text-emerald-700 tracking-wide flex items-center gap-1"><TrendingUp className="w-3 h-3" />Entradas</p>
                        <p className="font-bold text-emerald-800">+{comparison.executive.entradas_totales.toLocaleString()}</p>
                      </div>
                      <div className="bg-red-100 rounded p-2">
                        <p className="text-[10px] uppercase text-red-700 tracking-wide flex items-center gap-1"><TrendingDown className="w-3 h-3" />Salidas</p>
                        <p className="font-bold text-red-800">−{comparison.executive.salidas_totales.toLocaleString()}</p>
                      </div>
                      <div className={`rounded p-2 ${comparison.executive.neto >= 0 ? 'bg-purple-100' : 'bg-amber-100'}`}>
                        <p className="text-[10px] uppercase tracking-wide">Variación neta</p>
                        <p className={`font-bold ${comparison.executive.neto >= 0 ? 'text-purple-800' : 'text-amber-800'}`}>
                          {comparison.executive.neto >= 0 ? '+' : ''}{comparison.executive.neto.toLocaleString()} ({comparison.executive.variacion_pct >= 0 ? '+' : ''}{comparison.executive.variacion_pct}%)
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 text-xs text-slate-700">
                      <div className="bg-white/60 rounded p-2">
                        🔄 <strong>Tasa consumo:</strong> {comparison.executive.consumo_por_hora}/h
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        📦 <strong>Reabastecimiento:</strong> {comparison.executive.reabastecimiento_por_hora}/h
                      </div>
                      <div className={`rounded p-2 ${comparison.executive.requieren_reorden_b > 0 ? 'bg-amber-100' : 'bg-white/60'}`}>
                        🛒 <strong>Reorden urgente:</strong> {comparison.executive.requieren_reorden_b} SKUs
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ALERTAS OPERATIVAS */}
              {comparison.operational_alerts && comparison.operational_alerts.total_alertas > 0 && (
                <Card className="border-red-300 bg-red-50" data-testid="operational-alerts">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-red-800">
                      <AlertTriangle className="w-4 h-4" />
                      Alertas Operativas Nuevas
                      <Badge className="bg-red-600 text-white">{comparison.operational_alerts.total_alertas}</Badge>
                    </CardTitle>
                    <CardDescription className="text-xs text-red-700">SKUs que pasaron a estado problemático en este periodo</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    <AlertGroup label="Nuevos En Cero" items={comparison.operational_alerts.nuevas_en_cero} color="red" />
                    <AlertGroup label="Nuevos Bajo Mínimo" items={comparison.operational_alerts.nuevas_bajo_minimo} color="amber" />
                    <AlertGroup label="Nuevos Vencidos" items={comparison.operational_alerts.nuevas_vencidos} color="red" />
                    <AlertGroup label="Nuevos Por Vencer" items={comparison.operational_alerts.nuevas_por_vencer} color="orange" />
                  </CardContent>
                </Card>
              )}

              {/* MEJORAS */}
              {comparison.improvements && comparison.improvements.total_mejoras > 0 && (
                <Card className="border-emerald-300 bg-emerald-50" data-testid="improvements">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-emerald-800">
                      <Sparkles className="w-4 h-4" />
                      Mejoras
                      <Badge className="bg-emerald-600 text-white">{comparison.improvements.total_mejoras}</Badge>
                    </CardTitle>
                    <CardDescription className="text-xs text-emerald-700">SKUs cuyo estado mejoró en este periodo</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    <AlertGroup label="Salieron de En Cero" items={comparison.improvements.salieron_de_cero} color="emerald" />
                    <AlertGroup label="Salieron de Bajo Mínimo" items={comparison.improvements.salieron_de_bajo_minimo} color="emerald" />
                    <AlertGroup label="Resueltos (vencido/por vencer → OK)" items={comparison.improvements.resueltos} color="emerald" />
                  </CardContent>
                </Card>
              )}

              {/* TOP MOVERS */}
              {comparison.top_movers && (comparison.top_movers.top_consumidos.length > 0 || comparison.top_movers.top_reabastecidos.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="top-movers">
                  <TopMoversCard
                    title="Top 10 Consumidos"
                    icon={TrendingDown}
                    items={comparison.top_movers.top_consumidos}
                    color="red"
                  />
                  <TopMoversCard
                    title="Top 10 Reabastecidos"
                    icon={TrendingUp}
                    items={comparison.top_movers.top_reabastecidos}
                    color="emerald"
                  />
                </div>
              )}

              {/* Summary KPIs (compacto al final) */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <KpiCell label="SKUs comparados" value={comparison.summary.total_skus_comparados} color="slate" />
                <KpiCell label="Nuevos" value={comparison.summary.skus_nuevos} color="green" />
                <KpiCell label="Eliminados" value={comparison.summary.skus_eliminados} color="red" />
                <KpiCell label="Aumentaron" value={comparison.summary.skus_aumentaron} color="emerald" />
                <KpiCell label="Disminuyeron" value={comparison.summary.skus_disminuyeron} color="amber" />
                <KpiCell label="Δ Total" value={(comparison.summary.delta_total_unidades >= 0 ? '+' : '') + comparison.summary.delta_total_unidades} color="purple" />
              </div>

              {/* Botón exportar */}
              <div className="flex justify-end">
                <Button
                  onClick={handleCompareExport}
                  disabled={compareExporting}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="export-compare-btn"
                >
                  <Download className={`w-4 h-4 mr-2 ${compareExporting ? 'animate-pulse' : ''}`} />
                  {compareExporting ? 'Exportando…' : 'Exportar Excel'}
                </Button>
              </div>

              {/* Tabla de cambios */}
              <div className="border rounded overflow-x-auto max-h-[50vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="compare-table">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr className="text-left">
                      <th className="px-2 py-2 font-semibold">SKU</th>
                      <th className="px-2 py-2 font-semibold">Descripción</th>
                      <th className="px-2 py-2 font-semibold">Tipo</th>
                      <th className="px-2 py-2 font-semibold text-right">A</th>
                      <th className="px-2 py-2 font-semibold text-right">B</th>
                      <th className="px-2 py-2 font-semibold text-right">Δ</th>
                      <th className="px-2 py-2 font-semibold">Estado A</th>
                      <th className="px-2 py-2 font-semibold">Estado B</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {comparison.rows.filter(r => r.tipo_cambio !== 'sin_cambio').map((r, idx) => (
                      <ChangeRow key={`${r.sku}-${idx}`} row={r} />
                    ))}
                    {comparison.rows.filter(r => r.tipo_cambio !== 'sin_cambio').length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-6 text-slate-500">
                          No hay diferencias entre estos dos snapshots.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-componentes auxiliares para el modal
const KpiCell = ({ label, value, color }) => {
  const map = {
    slate: 'bg-slate-50 text-slate-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
  };
  return (
    <div className={`${map[color] || map.slate} rounded p-2 text-center`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] uppercase tracking-wide">{label}</p>
    </div>
  );
};

const ChangeRow = ({ row }) => {
  const tipoBadge = {
    nuevo: { label: 'Nuevo', cls: 'bg-green-100 text-green-700', Icon: Plus },
    eliminado: { label: 'Eliminado', cls: 'bg-red-100 text-red-700', Icon: Minus },
    aumento: { label: 'Aumento', cls: 'bg-emerald-100 text-emerald-700', Icon: Plus },
    disminucion: { label: 'Disminución', cls: 'bg-amber-100 text-amber-700', Icon: Minus },
  };
  const t = tipoBadge[row.tipo_cambio] || tipoBadge.aumento;
  const TIcon = t.Icon;
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-2 py-1.5 font-mono font-semibold">{row.sku}</td>
      <td className="px-2 py-1.5 max-w-xs truncate" title={row.descripcion}>{row.descripcion || '—'}</td>
      <td className="px-2 py-1.5">
        <Badge variant="outline" className={t.cls}>
          <TIcon className="w-3 h-3 mr-1" />
          {t.label}
        </Badge>
      </td>
      <td className="px-2 py-1.5 text-right font-mono">{row.unidades_a}</td>
      <td className="px-2 py-1.5 text-right font-mono">{row.unidades_b}</td>
      <td className={`px-2 py-1.5 text-right font-mono font-bold ${row.delta > 0 ? 'text-emerald-700' : row.delta < 0 ? 'text-red-700' : 'text-slate-500'}`}>
        {row.delta > 0 ? '+' : ''}{row.delta}
      </td>
      <td className="px-2 py-1.5 text-xs text-slate-600">{row.estado_a || '—'}</td>
      <td className={`px-2 py-1.5 text-xs ${row.cambio_estado ? 'font-bold text-purple-700' : 'text-slate-600'}`}>
        {row.estado_b || '—'}
      </td>
    </tr>
  );
};

// Sub-componente: grupo de alertas/mejoras (lista colapsable de SKUs)
const AlertGroup = ({ label, items, color }) => {
  if (!items || items.length === 0) return null;
  const colors = {
    red: 'border-red-300 bg-red-100/50 text-red-800',
    amber: 'border-amber-300 bg-amber-100/50 text-amber-800',
    orange: 'border-orange-300 bg-orange-100/50 text-orange-800',
    emerald: 'border-emerald-300 bg-emerald-100/50 text-emerald-800',
  };
  return (
    <div className={`border rounded p-2 ${colors[color] || colors.amber}`}>
      <p className="text-xs font-semibold mb-1">{label} ({items.length})</p>
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 12).map((it) => (
          <span
            key={it.sku}
            className="bg-white border rounded px-2 py-0.5 text-xs font-mono"
            title={`${it.descripcion} · A:${it.unidades_a} → B:${it.unidades_b} (Δ${it.delta})`}
          >
            {it.sku}
          </span>
        ))}
        {items.length > 12 && (
          <span className="text-xs text-slate-600 italic">+{items.length - 12} más…</span>
        )}
      </div>
    </div>
  );
};

// Sub-componente: tarjeta de top movers (consumos o reabastecimientos)
const TopMoversCard = ({ title, icon: Icon, items, color }) => {
  if (!items || items.length === 0) return null;
  const colorMap = {
    red: 'text-red-700 border-red-200 bg-red-50',
    emerald: 'text-emerald-700 border-emerald-200 bg-emerald-50',
  };
  const cls = colorMap[color] || colorMap.emerald;
  const max = Math.max(...items.map((it) => Math.abs(it.delta || 0)), 1);
  return (
    <Card className={cls}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-1">
        {items.map((it) => {
          const pct = Math.min(100, (Math.abs(it.delta) / max) * 100);
          return (
            <div key={it.sku} className="text-xs">
              <div className="flex justify-between items-center mb-0.5">
                <span className="font-mono font-semibold truncate flex-1" title={it.descripcion}>{it.sku}</span>
                <span className="font-bold ml-2">{it.delta > 0 ? '+' : ''}{it.delta}</span>
              </div>
              <div className="h-1.5 bg-white/70 rounded overflow-hidden">
                <div
                  className={`h-full rounded ${color === 'red' ? 'bg-red-500' : 'bg-emerald-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default HistorialConsigna;
